'use client';

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { COUNTRIES } from '@/lib/countries';
import { Input } from './input';

export interface CountrySelectProps {
  /** English country name; '' means none. A value outside the list is shown as typed. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** Input placeholder (translated by the caller). */
  placeholder?: string;
  /** Shown when the search matches nothing (translated by the caller). */
  noMatches: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Matches by name (substring) or ISO code (prefix); an exact code, then a name prefix, rank first. */
function search(query: string) {
  const q = fold(query);
  if (!q) return COUNTRIES;
  const ranked: { rank: number; index: number }[] = [];
  COUNTRIES.forEach((country, index) => {
    const name = fold(country.name);
    const code = country.code.toLowerCase();
    let rank = -1;
    if (code === q) rank = 0;
    else if (name.startsWith(q)) rank = 1;
    else if (name.includes(q)) rank = 2;
    else if (q.length < 3 && code.startsWith(q)) rank = 3;
    if (rank >= 0) ranked.push({ rank, index });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return ranked.map((r) => COUNTRIES[r.index]);
}

export function CountrySelect({
  value,
  onChange,
  label,
  placeholder,
  noMatches,
  id,
  disabled,
  className,
}: CountrySelectProps) {
  const generatedId = useId();
  const baseId = id || generatedId;
  const listId = `${baseId}-listbox`;
  const optionId = (code: string) => `${baseId}-option-${code}`;

  // `text` only matters while the user is typing; otherwise the input shows the stored value.
  const [text, setText] = useState('');
  const [typing, setTyping] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  const shown = typing ? text : value;
  const results = useMemo(() => search(typing ? text : ''), [typing, text]);
  const expanded = open && results.length > 0;

  useEffect(() => {
    if (!expanded || active < 0) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, expanded]);

  function close() {
    setOpen(false);
    setTyping(false);
    setActive(-1);
  }

  function choose(name: string) {
    onChange(name);
    close();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setText(next);
    setTyping(true);
    setOpen(true);
    setActive(search(next).length > 0 ? 0 : -1);
    if (next === '') onChange('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActive(0);
        } else if (results.length > 0) {
          setActive((i) => (i + 1) % results.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActive(results.length - 1);
        } else if (results.length > 0) {
          setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
        }
        break;
      case 'Enter':
        // Only claim Enter while a row is highlighted, so a closed field still submits its form.
        if (expanded && active >= 0 && results[active]) {
          e.preventDefault();
          choose(results[active].name);
        }
        break;
      case 'Escape':
        if (open || typing) {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
        break;
    }
  }

  function handleBlur() {
    if (typing) {
      // Tabbing away from an exactly typed name keeps it; anything else reverts to the stored value.
      const exact = COUNTRIES.find((c) => fold(c.name) === fold(text));
      if (exact && exact.name !== value) onChange(exact.name);
    }
    close();
  }

  return (
    <div className={cn('relative', className)}>
      <Input
        id={baseId}
        label={label}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={
          expanded && active >= 0 && results[active] ? optionId(results[active].code) : undefined
        }
        placeholder={placeholder}
        disabled={disabled}
        value={shown}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={() => !disabled && setOpen(true)}
      />
      {expanded && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          className="border-border bg-background absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border py-1 text-sm shadow-lg"
        >
          {results.map((country, i) => (
            <li
              key={country.code}
              id={optionId(country.code)}
              role="option"
              aria-selected={i === active}
              // Keep focus in the input so the click lands before the blur handler closes the list.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(country.name)}
              onMouseMove={() => i !== active && setActive(i)}
              className={cn(
                'text-foreground flex cursor-pointer items-center justify-between px-3 py-2',
                i === active && 'bg-surface',
                country.name === value && 'font-medium',
              )}
            >
              <span>{country.name}</span>
              <span aria-hidden="true" className="text-muted text-xs">
                {country.code}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && results.length === 0 && (
        <div
          role="status"
          className="border-border bg-background text-muted absolute z-20 mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-lg"
        >
          {noMatches}
        </div>
      )}
    </div>
  );
}
